import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import {
  CmsDynamicFeatureCopy,
  CmsDynamicFlows,
  CmsDynamicKeys, CmsDynamicPageSections,
  CmsDynamicSubFlows,
  CmsFeatureCopy,
  CmsKeys,
  ContentPageKeys, DynamicPageResponse,
  FeaturePageKeys,
} from '@constants/cms.constants';
import { ApiResponse } from '@models/responses/api-response.model';
import { EnvironmentService } from '@services/environment-service/environment.service';
import { DisruptionBanner } from '@models/cms/disruption-banner.model';
import {DynamicPageConfig} from "@models/cms/dynamic-pages-umbraco/dynamic-pages-config.model";

type ContentPageResponse = {
  cmsExternalId: string;
  cultureIsoCode: string;
  name: string;
  content: object;
}

type FeaturePageResponse = {
  cmsExternalId: string;
  cultureIsoCode: string;
  name: string;
  values: object;
}

@Injectable({
  providedIn: 'root',
})
export class CmsService {
  /**
   * Gets the content for the given CMS key
   * as a JavaScript object.
   *
   * @param key The CMS key
   * @returns The observable for the data that will be returned, or the observable
   * with the actual data if it has already been cached. Neither the observable
   * nor the data are ever duplicated for a given culture/key.
   */
  public getFeature(
    flow: string,
    key: CmsKeys,
    culture: string,
  ): Observable<CmsFeatureCopy> {
    return this.http
      .get<ApiResponse<CmsFeatureCopy>>(
        `${EnvironmentService.environment.cmsBaseUrl}${culture}/${flow}/${key}`,
      )
      .pipe(map((res) => res.data));
  }

  public getContentPage(
    contentPageName: ContentPageKeys,
    culture: string,
  ): Observable<CmsFeatureCopy> {
    return this.http
      .post<ApiResponse<ContentPageResponse[]>>(
        `${EnvironmentService.environment.cmsUmbracoBaseUrl}content-pages/${culture}`,
        {
          contentPages: [contentPageName],
        },
      )
      .pipe(
        map((res) =>
          res.data.find(
            (el) => el.name === contentPageName.toLocaleLowerCase(),
          )?.content as CmsFeatureCopy,
        ),
      );
  }

  public getFeaturePage(
    featureName: FeaturePageKeys,
    culture: string,
  ): Observable<CmsFeatureCopy> {
    return this.http
      .post<ApiResponse<FeaturePageResponse[]>>(
        `${EnvironmentService.environment.cmsUmbracoBaseUrl}features/${culture}`,
        {
          features: [featureName],
        },
      )
      .pipe(
        map((res) =>
          res.data.find(
            (el) => el.name === featureName.toLocaleLowerCase(),
          )?.values as CmsFeatureCopy,
        ),
      );
  }

  public getDynamicPage(
    dynamicPageName: string,
    culture: string,
  ): Observable<DynamicPageResponse> {
    return this.http
      .post<ApiResponse<DynamicPageResponse[]>>(
        `${EnvironmentService.environment.cmsUmbracoBaseUrl}dynamic-pages/${culture}`,
        {
          dynamicPages: [dynamicPageName],
        },
      )
      .pipe(
        map((res) =>
          res.data.find(
            (el) => el.name === dynamicPageName.toLocaleLowerCase(),
          ) as DynamicPageResponse,
        ),
      );
  }

  public getFeatureLocal(
    flow: string,
    key: CmsKeys,
  ): Observable<CmsFeatureCopy> {
    return this.http
      .get<ApiResponse<CmsFeatureCopy>>(
        `${EnvironmentService.environment.cmsFallbackUrl}${flow}/${key}.json`,
      )
      .pipe(map((res) => res.data));
  }

  public getFlow(
    flowKey: CmsKeys,
    culture: string,
  ): Observable<CmsFeatureCopy> {
    return this.http
      .get<ApiResponse<CmsFeatureCopy>>(
        `${EnvironmentService.environment.cmsBaseUrl}${culture}/${flowKey}`,
      )
      .pipe(map((res) => res.data));
  }

  public getFlowLocal(flowKey: CmsKeys | ContentPageKeys | FeaturePageKeys | string): Observable<CmsFeatureCopy> {
    return this.http
      .get<ApiResponse<CmsFeatureCopy>>(
        `${EnvironmentService.environment.cmsFallbackUrl}${flowKey}.json`,
      )
      .pipe(map((res) => res.data));
  }

  public getDynamicFeature(
    flowKey: CmsDynamicFlows,
    subFlowKey: CmsDynamicSubFlows | undefined,
    cmsKey: CmsDynamicKeys | undefined,
    culture: string,
  ): Observable<CmsDynamicFeatureCopy> {
    return this.http
      .get<ApiResponse<CmsDynamicFeatureCopy>>(
        cmsKey
          ? `${EnvironmentService.environment.cmsBaseUrl}${culture}/${flowKey}/${subFlowKey}/${cmsKey}`
          : subFlowKey
          ? `${EnvironmentService.environment.cmsBaseUrl}${culture}/${flowKey}/${subFlowKey}`
          : `${EnvironmentService.environment.cmsBaseUrl}${culture}/${flowKey}`,
      )
      .pipe(map((res) => res.data));
  }

  public getDynamicFeatureLocal(
    flow: CmsDynamicFlows,
    subFlow?: CmsDynamicSubFlows,
    key?: CmsDynamicKeys,
  ): Observable<CmsDynamicFeatureCopy> {
    return this.http.get<CmsDynamicFeatureCopy>(
      key
        ? `${EnvironmentService.environment.cmsFallbackUrl}${flow}/${subFlow}/${key}.json`
        : `${EnvironmentService.environment.cmsFallbackUrl}${flow}/${subFlow}.json`,
    );
  }

  getDisruptionBanners(
    culture?: string,
  ): Observable<ApiResponse<DisruptionBanner[]>>{
    return this.http
      .get<ApiResponse<DisruptionBanner[]>>(
        `${EnvironmentService.environment.cmsUmbracoBaseUrl}disruptions/${culture}`,
      )
  }

  getDynamicPagesConfig(
  ): Observable<ApiResponse<DynamicPageConfig[]>>{
    return this.http
      .get<ApiResponse<DynamicPageConfig[]>>(
        `${EnvironmentService.environment.cmsUmbracoBaseUrl}dynamic-pages/all`,
      )
  }

  constructor(private http: HttpClient) {}
}
